import {
  THEME_BASE_PREFIX,
  THEME_TOKEN_PREFIX,
  type ThemeBaseColors,
  type ThemeDefinition,
  type ThemeTokens,
} from '@hibro/shared';

/** id of the injected `<style>` carrying the base (primitive) custom properties. */
const BASE_STYLE_ID = 'app-theme-base';
/** id of the injected `<style>` carrying the semantic token custom properties. */
const TOKENS_STYLE_ID = 'app-theme-tokens';

/**
 * Apply a theme to a document (见 13 / §5.10). Two-tier: the base
 * (primitive) palette as `--app-base-<name>` custom properties, then the
 * semantic tokens as `--app-<name>` — most token values are
 * `var(--app-base-<name>)` references resolved against the base layer. Stamps
 * `data-theme="light|dark"` for any type-conditional styling. The theme is the
 * single source of truth (shared `BUILTIN_THEMES`); the renderer just paints it
 * — so the same call drives the in-client workbench and detached windows.
 *
 * The variables are emitted into injected `<style>` rules (`:root { … }`), NOT
 * written as inline custom properties on the element — there are ~150 base +
 * ~60 token vars, and stamping them all onto the element's `style` attribute
 * bloats the DOM. See {@link applyBaseColors} / {@link applyThemeTokens}.
 */
export function applyTheme(
  theme: ThemeDefinition,
  root: HTMLElement = document.documentElement,
  platform?: 'mac' | 'win',
): void {
  root.dataset.theme = theme.type;
  // `data-platform` drives the Mac/Win split tokens (见 13 / §5.10): the
  // tokens layer defines `-mac` / `-win` variants and `main.css` resolves the
  // un-suffixed `--app-color-bg-module-*` against the active platform. The
  // attribute is only stamped when a platform is supplied, so callers without
  // OS detection keep the CSS default (mac).
  if (platform) {
    root.dataset.platform = platform;
  }
  applyBaseColors(theme.base, root);
  applyThemeTokens(theme.tokens, root);
}

/** Apply the base (primitive) palette as `--app-base-<name>` custom properties. */
export function applyBaseColors(
  base: ThemeBaseColors,
  root: HTMLElement = document.documentElement,
): void {
  injectVars(root, BASE_STYLE_ID, THEME_BASE_PREFIX, base);
}

/** Apply a bare token map (no `type`/base); used by iframe content via the SDK,
 * where the tokens have already been flattened to final color values. */
export function applyThemeTokens(
  tokens: ThemeTokens,
  root: HTMLElement = document.documentElement,
): void {
  injectVars(root, TOKENS_STYLE_ID, THEME_TOKEN_PREFIX, tokens);
}

/**
 * Emit a `:root { --<prefix><name>: <value>; … }` rule into a single `<style>`
 * element (created once per `styleId`, then updated in place on re-apply) in the
 * root's document `<head>`. Keeps the custom properties off the element's inline
 * `style` attribute. `:root` matches the document's root element, so passing any
 * element from a given document (`root.ownerDocument`) scopes the vars to it.
 */
function injectVars(
  root: HTMLElement,
  styleId: string,
  prefix: string,
  vars: Record<string, string>,
): void {
  const doc = root.ownerDocument;
  const body = Object.entries(vars)
    .map(([name, value]) => `  ${prefix}${name}: ${value};`)
    .join('\n');
  const css = `:root {\n${body}\n}`;
  let style = doc.getElementById(styleId);
  if (!(style instanceof HTMLStyleElement)) {
    style = doc.createElement('style');
    style.id = styleId;
    (doc.head ?? doc.documentElement).appendChild(style);
  }
  style.textContent = css;
}
